import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import ContainerAnimation from "components/ContainerAnimation";

import RedeFilters from "./components/RedeFilters";
import RedeProfessionalsTable from "./components/RedeProfessionalsTable";
import RedeTransactionsTable from "./components/RedeTransactionsTable";
import { buildDemoData } from "./demoData";
import { TimeFrame } from "./timeFrame";
import { useRedeData } from "./useRedeData";

// Fallback de desenvolvimento: troque pelo companyId da Rede Cross no seu
// ambiente, ou acesse a página com ?empresa=<id>. Quando a auth de rede existir,
// o companyId virá do token/perfil e este fallback deixa de ser usado.
const FALLBACK_COMPANY_ID = 0;

export default function RedeDashboard() {
  const [searchParams] = useSearchParams();
  const empresaParam = searchParams.get("empresa");
  // Modo demonstração: ?empresa=demo carrega um dataset fictício local, sem
  // tocar na API — para apresentação ao cliente.
  const isDemo = empresaParam === "demo";
  const companyId = Number(empresaParam) || FALLBACK_COMPANY_ID;

  const [timeFrame, setTimeFrame] = useState<{
    value: TimeFrame;
    label: string;
  }>({ value: TimeFrame.LastWeek, label: "Última semana" });
  const [selectedDoctorId, setSelectedDoctorId] = useState<number | null>(null);

  const realData = useRedeData(companyId, timeFrame.value, selectedDoctorId);
  const demoData = useMemo(
    () => buildDemoData(timeFrame.value, selectedDoctorId),
    [timeFrame.value, selectedDoctorId]
  );

  const {
    professionals,
    perProfessional,
    transactions,
    isLoading,
    error,
    isTruncated,
  } = isDemo ? demoData : realData;

  // A instituição vê os profissionais que efetivamente geraram orçamentos.
  const generators = useMemo(
    () => perProfessional.filter((p) => p.count > 0),
    [perProfessional]
  );

  const hasCompany = isDemo || companyId > 0;

  return (
    <ContainerAnimation className="content">
      <div className="bg-white">
        <div className="container">
          <div className="row justify-content-between align-items-center gx-2 pb-4">
            <div className="col-12 col-lg-auto pb-2 d-flex align-items-center gap-2">
              <h1 className="mb-0">Painel da Rede</h1>
              {isDemo && (
                <span className="badge bg-warning text-dark align-self-center">
                  Demonstração
                </span>
              )}
            </div>
            {hasCompany && (
              <div className="col-12 col-lg-8">
                <RedeFilters
                  timeFrame={timeFrame}
                  setTimeFrame={setTimeFrame}
                  doctors={professionals}
                  selectedDoctorId={selectedDoctorId}
                  setSelectedDoctorId={setSelectedDoctorId}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="container d-flex flex-column gap-4">
        {!hasCompany && (
          <div className="alert alert-info mb-0 d-flex align-items-center gap-2" role="alert">
            <i className="uil uil-info-circle fs-5 flex-shrink-0" aria-hidden="true"></i>
            <span>
              Informe a instituição na URL: <code>?empresa=&lt;id&gt;</code>.
            </span>
          </div>
        )}

        {hasCompany && error && (
          <div className="alert alert-danger mb-0 d-flex align-items-center gap-2" role="alert">
            <i className="uil uil-exclamation-octagon fs-5 flex-shrink-0" aria-hidden="true"></i>
            <span>
              Não foi possível carregar os dados da rede. Verifique sua conexão e
              tente alterar o período.
            </span>
          </div>
        )}

        {hasCompany && (
          <>
            <RedeProfessionalsTable rows={generators} isLoading={isLoading} />
            <RedeTransactionsTable
              rows={transactions}
              isLoading={isLoading}
              isTruncated={isTruncated}
            />
          </>
        )}
      </div>
    </ContainerAnimation>
  );
}
